import { useSearchParams } from '@remix-run/react'
import { useCallback, useEffect, useRef } from 'react'

export function useOnboardingShake<T extends HTMLElement | null>(
  id: string,
  config: { timeout?: number; shouldScroll?: boolean } = {}
) {
  const containerRef = useRef<T>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [params, setParams] = useSearchParams()

  const triggerShakeAnimation = useCallback(() => {
    const element = containerRef.current
    if (!element) return

    // Remove the animation class if it exists
    element.classList.remove('animate-shake')

    // Force a reflow to ensure the class removal takes effect
    void element.offsetHeight

    // Add the animation class to start the animation
    element.classList.add('animate-shake')

    setParams((prev) => {
      prev.delete('onboarding')
      return prev
    })
  }, [setParams])

  useEffect(() => {
    if (params.get('onboarding') === id) {
      if (config.shouldScroll ?? typeof config.shouldScroll === 'undefined') {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' })
      }

      timeoutRef.current = setTimeout(
        triggerShakeAnimation,
        config.timeout ?? 800
      )
    }

    // Cleanup function to clear timeout when effect re-runs or component unmounts
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [id, config, params, setParams, triggerShakeAnimation])

  return containerRef
}
